import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ReportCategory, ReportSubCategory } from './reports.types';

export interface FavoriteReport {
  category: ReportCategory;
  subCategory: ReportSubCategory;
  label: string;
}

@Injectable({
  providedIn: 'root'
})
export class FavoritesService {
  private readonly STORAGE_KEY = 'favorite-reports';
  private favoritesSubject = new BehaviorSubject<FavoriteReport[]>([]);
  private saveTimeout: any;

  constructor(private http: HttpClient) {
    this.loadFavorites();
  }

  get favorites$() {
    return this.favoritesSubject.asObservable();
  }

  get favorites() {
    return this.favoritesSubject.value;
  }

  isFavorite(category: ReportCategory, subCategory: ReportSubCategory): boolean {
    return this.favorites.some(f => f.category === category && f.subCategory === subCategory);
  }

  toggleFavorite(category: ReportCategory, subCategory: ReportSubCategory, label: string): void {
    console.log('toggleFavorite called:', category, subCategory, label);
    const favorites = [...this.favorites];
    const index = favorites.findIndex(f => f.category === category && f.subCategory === subCategory);
    
    if (index >= 0) {
      favorites.splice(index, 1);
    } else {
      favorites.push({ category, subCategory, label });
    }
    
    this.saveFavorites(favorites);
  }

  private loadFavorites(): void {
    const apiUrl = `api/userPrefs/favourite_reports`;
    this.http.get<{userPrefs: FavoriteReport[]}>(apiUrl).subscribe({
      next: (response) => {
        this.favoritesSubject.next(response.userPrefs || []);
      },
      error: () => {
        // Fallback to localStorage if API fails
        const stored = localStorage.getItem(this.STORAGE_KEY);
        const favorites = stored ? JSON.parse(stored) : [];
        this.favoritesSubject.next(favorites);
      }
    });
  }

  private saveFavorites(favorites: FavoriteReport[]): void {
    // Update UI immediately
    this.favoritesSubject.next(favorites);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(favorites));
    
    // Debounce API calls
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    
    this.saveTimeout = setTimeout(() => {
      const apiUrl = `api/userPrefs/favourite_reports`;
      const payload = { userPrefs: favorites };
      
      this.http.put(apiUrl, payload).subscribe({
        next: () => {
          console.log('Favorites saved to API successfully');
        },
        error: (error) => {
          console.error('Failed to save favorites to API:', error);
        }
      });
    }, 500);
  }
}